/**
 * ShareMenu component
 *
 * Dropdown menu with options to share session as Instagram story or PDF.
 */

import { useState, useRef, useEffect } from 'react';

interface ShareMenuProps {
  sessionUuid: string;
  disabled?: boolean;
}

export function ShareMenu({ sessionUuid, disabled = false }: ShareMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState<'story' | 'pdf' | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShareStory = async () => {
    setIsGenerating('story');
    try {
      const response = await fetch(`/api/share/story/${sessionUuid}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to generate story');
      }

      // Get blob and ensure it's typed as PNG
      const rawBlob = await response.blob();
      const blob = new Blob([rawBlob], { type: 'image/png' });

      // Check if mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      // Try Web Share API first (works on mobile for sharing images)
      if (isMobile && navigator.share && navigator.canShare) {
        // Use simple filename for better iOS compatibility
        const file = new File([blob], 'lucy_story.png', {
          type: 'image/png',
          lastModified: Date.now()
        });

        const shareData = {
          files: [file],
          title: 'Lucy Astro Story'
        };

        if (navigator.canShare(shareData)) {
          try {
            // Close menu before sharing to avoid UI issues
            setIsGenerating(null);
            setIsOpen(false);

            await navigator.share(shareData);
            return; // Success - exit early
          } catch (shareError) {
            // User cancelled or share failed - fall through to download
            if ((shareError as Error).name !== 'AbortError') {
              console.log('Share API failed, falling back to download');
            } else {
              // User cancelled - just return, menu already closed
              return;
            }
          }
        }
      }

      // Fallback: Download the image
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lucy_story.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Show instruction for mobile users
      if (isMobile) {
        alert('Slika je sacuvana! Otvori Instagram i dodaj je kao Story.');
      }
    } catch (error) {
      console.error('Share story error:', error);
      alert('Greska pri generisanju story-ja. Pokusaj ponovo.');
    } finally {
      setIsGenerating(null);
      setIsOpen(false);
    }
  };

  const handleSharePdf = async () => {
    setIsGenerating('pdf');
    try {
      const response = await fetch(`/api/share/pdf/${sessionUuid}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const rawBlob = await response.blob();
      const blob = new Blob([rawBlob], { type: 'application/pdf' });
      const filename = `lucy-chat-${sessionUuid.slice(0, 8)}.pdf`;

      // Check if mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      // Try Web Share API first (works on mobile for sharing files)
      if (isMobile && navigator.share && navigator.canShare) {
        const file = new File([blob], filename, {
          type: 'application/pdf',
          lastModified: Date.now()
        });

        const shareData = {
          files: [file],
          title: 'Lucy Astrologija - PDF'
        };

        if (navigator.canShare(shareData)) {
          try {
            // Close menu before sharing to avoid UI issues
            setIsGenerating(null);
            setIsOpen(false);

            await navigator.share(shareData);
            return; // Success - exit early
          } catch (shareError) {
            // User cancelled or share failed - fall through to download
            if ((shareError as Error).name !== 'AbortError') {
              console.log('Share API failed, falling back to download');
            } else {
              // User cancelled - just return, menu already closed
              return;
            }
          }
        }
      }

      // Fallback: Download the PDF
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Share PDF error:', error);
      alert('Greska pri generisanju PDF-a. Pokusaj ponovo.');
    } finally {
      setIsGenerating(null);
      setIsOpen(false);
    }
  };

  return (
    <div className="share-menu-container" ref={menuRef}>
      <button
        className="share-btn"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        title="Podeli"
        aria-label="Podeli sesiju"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
      </button>

      {isOpen && (
        <div className="share-dropdown">
          <button
            className="share-option"
            onClick={handleShareStory}
            disabled={isGenerating !== null}
          >
            {isGenerating === 'story' ? (
              <span className="share-loading">Generisem...</span>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
                <span>Instagram Story</span>
              </>
            )}
          </button>

          <button
            className="share-option"
            onClick={handleSharePdf}
            disabled={isGenerating !== null}
          >
            {isGenerating === 'pdf' ? (
              <span className="share-loading">Generisem...</span>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>PDF Export</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default ShareMenu;
